import {
  CompanyFormData,
  SeasonFormData,
  OwnerFormData,
  FieldEmployeesFormData,
  AdminEmployeesFormData,
  TrucksFormData,
  TrailersFormData,
  HeavyMachineryFormData,
  FieldEmployee,
  AdminEmployee,
  Truck,
  Trailer,
  HeavyMachinery
} from './types'
import {
  DEFAULT_COMPANY_DATA,
  DEFAULT_SEASON_DATA,
  DEFAULT_OWNER_DATA,
  DEFAULT_FIELD_EMPLOYEES_DATA,
  DEFAULT_ADMIN_EMPLOYEES_DATA,
  DEFAULT_TRUCKS_DATA,
  DEFAULT_TRAILERS_DATA,
  DEFAULT_HEAVY_MACHINERY_DATA,
  MAX_ENTITIES,
  createDefaultAdminEmployee,
  createDefaultTruck,
  createDefaultTrailer,
  createDefaultHeavyMachinery,
  getHeavyMachineryDefaults
} from './constants'
import { growFieldRoster, withCrewHoursPerWeek } from './budgetBuilderModel'

// State type
export type BudgetBuilderReducerState = {
  currentStep: number
  companyData: CompanyFormData
  seasonData: SeasonFormData
  ownerData: OwnerFormData
  fieldEmployeesData: FieldEmployeesFormData
  adminEmployeesData: AdminEmployeesFormData
  trucksData: TrucksFormData
  trailersData: TrailersFormData
  heavyMachineryData: HeavyMachineryFormData
}

// Action types
export type BudgetBuilderAction =
  | { type: 'SET_STEP'; payload: number }
  | { type: 'NEXT_STEP' }
  | { type: 'PREV_STEP' }
  | { type: 'RESET' }
  | { type: 'UPDATE_COMPANY_DATA'; field: keyof CompanyFormData; value: any }
  | { type: 'UPDATE_SEASON_DATA'; field: keyof SeasonFormData; value: any }
  | { type: 'UPDATE_OWNER_DATA'; field: keyof OwnerFormData; value: any }
  | { type: 'SET_CREW_HOURS_PER_WEEK'; hoursPerWeek: number }
  | { type: 'SET_FIELD_EMPLOYEE_COUNT'; count: number | null }
  | { type: 'UPDATE_FIELD_EMPLOYEE'; index: number; field: keyof FieldEmployee; value: any }
  | { type: 'ADD_FIELD_EMPLOYEE' }
  | { type: 'REMOVE_FIELD_EMPLOYEE'; index: number }
  | { type: 'SET_ADMIN_EMPLOYEE_COUNT'; count: number | null }
  | { type: 'UPDATE_ADMIN_EMPLOYEE'; index: number; field: keyof AdminEmployee; value: any }
  | { type: 'ADD_ADMIN_EMPLOYEE' }
  | { type: 'REMOVE_ADMIN_EMPLOYEE'; index: number }
  | { type: 'SET_TRUCK_COUNT'; count: number | null }
  | { type: 'UPDATE_TRUCK'; index: number; field: keyof Truck; value: any }
  | { type: 'ADD_TRUCK' }
  | { type: 'REMOVE_TRUCK'; index: number }
  | { type: 'SET_TRAILER_COUNT'; count: number | null }
  | { type: 'UPDATE_TRAILER'; index: number; field: keyof Trailer; value: any }
  | { type: 'ADD_TRAILER' }
  | { type: 'REMOVE_TRAILER'; index: number }
  | { type: 'SET_HEAVY_MACHINERY_COUNT'; count: number | null }
  | { type: 'UPDATE_HEAVY_MACHINERY'; index: number; field: keyof HeavyMachinery; value: any }
  | { type: 'ADD_HEAVY_MACHINERY' }
  | { type: 'REMOVE_HEAVY_MACHINERY'; index: number }

// Initial state
export const getInitialState = (): BudgetBuilderReducerState => ({
  currentStep: 0,
  companyData: DEFAULT_COMPANY_DATA,
  seasonData: DEFAULT_SEASON_DATA,
  ownerData: DEFAULT_OWNER_DATA,
  fieldEmployeesData: DEFAULT_FIELD_EMPLOYEES_DATA,
  adminEmployeesData: DEFAULT_ADMIN_EMPLOYEES_DATA,
  trucksData: DEFAULT_TRUCKS_DATA,
  trailersData: DEFAULT_TRAILERS_DATA,
  heavyMachineryData: DEFAULT_HEAVY_MACHINERY_DATA
})

// Helper: Sync array with count for field employees
const syncFieldEmployees = (data: FieldEmployeesFormData, seasonHoursPerWeek: number | null = 40): FieldEmployeesFormData => {
  const targetCount = data.employeeCount ?? 0

  if (targetCount === data.employees.length) {
    return data
  }

  return { ...data, employees: growFieldRoster(data.employees, targetCount, seasonHoursPerWeek) }
}

// Helper: Sync array with count for admin employees
const syncAdminEmployees = (data: AdminEmployeesFormData): AdminEmployeesFormData => {
  const currentCount = data.employees.length
  const targetCount = data.employeeCount ?? 0

  if (targetCount === currentCount) return data

  if (targetCount > currentCount) {
    const newEmployees = [...data.employees]
    for (let i = currentCount; i < targetCount; i++) {
      newEmployees.push(createDefaultAdminEmployee(i))
    }
    return { ...data, employees: newEmployees }
  } else {
    return { ...data, employees: data.employees.slice(0, targetCount) }
  }
}

// Helper: Sync array with count for trucks
const syncTrucks = (data: TrucksFormData): TrucksFormData => {
  const currentCount = data.trucks.length
  const targetCount = data.truckCount ?? 0

  if (targetCount === currentCount) return data

  if (targetCount > currentCount) {
    const newTrucks = [...data.trucks]
    for (let i = currentCount; i < targetCount; i++) {
      newTrucks.push(createDefaultTruck(i))
    }
    return { ...data, trucks: newTrucks }
  } else {
    return { ...data, trucks: data.trucks.slice(0, targetCount) }
  }
}

// Helper: Sync array with count for trailers
const syncTrailers = (data: TrailersFormData): TrailersFormData => {
  const currentCount = data.trailers.length
  const targetCount = data.trailerCount ?? 0

  if (targetCount === currentCount) return data

  if (targetCount > currentCount) {
    const newTrailers = [...data.trailers]
    for (let i = currentCount; i < targetCount; i++) {
      newTrailers.push(createDefaultTrailer(i))
    }
    return { ...data, trailers: newTrailers }
  } else {
    return { ...data, trailers: data.trailers.slice(0, targetCount) }
  }
}

// Helper: Sync array with count for heavy machinery
const syncHeavyMachinery = (data: HeavyMachineryFormData): HeavyMachineryFormData => {
  const currentCount = data.machinery.length
  const targetCount = data.machineryCount ?? 0

  if (targetCount === currentCount) return data

  if (targetCount > currentCount) {
    const newMachinery = [...data.machinery]
    for (let i = currentCount; i < targetCount; i++) {
      newMachinery.push(createDefaultHeavyMachinery(i))
    }
    return { ...data, machinery: newMachinery }
  } else {
    return { ...data, machinery: data.machinery.slice(0, targetCount) }
  }
}

// Reducer
export const budgetBuilderReducer = (
  state: BudgetBuilderReducerState,
  action: BudgetBuilderAction
): BudgetBuilderReducerState => {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, currentStep: action.payload }

    case 'NEXT_STEP':
      return { ...state, currentStep: state.currentStep + 1 }

    case 'PREV_STEP':
      return { ...state, currentStep: Math.max(0, state.currentStep - 1) }

    case 'RESET':
      return getInitialState()

    case 'UPDATE_COMPANY_DATA':
      return {
        ...state,
        companyData: { ...state.companyData, [action.field]: action.value }
      }

    case 'UPDATE_SEASON_DATA':
      return {
        ...state,
        seasonData: { ...state.seasonData, [action.field]: action.value }
      }

    case 'UPDATE_OWNER_DATA':
      return {
        ...state,
        ownerData: { ...state.ownerData, [action.field]: action.value }
      }

    // Field Employees
    case 'SET_CREW_HOURS_PER_WEEK':
      return withCrewHoursPerWeek(state, action.hoursPerWeek)

    case 'SET_FIELD_EMPLOYEE_COUNT': {
      const capped = action.count === null ? null : Math.min(action.count, MAX_ENTITIES)
      const newData = { ...state.fieldEmployeesData, employeeCount: capped }
      return {
        ...state,
        fieldEmployeesData: syncFieldEmployees(newData, state.seasonData.hoursPerWeek)
      }
    }

    case 'UPDATE_FIELD_EMPLOYEE': {
      const newEmployees = [...state.fieldEmployeesData.employees]
      newEmployees[action.index] = { ...newEmployees[action.index], [action.field]: action.value }
      return {
        ...state,
        fieldEmployeesData: { ...state.fieldEmployeesData, employees: newEmployees }
      }
    }

    case 'ADD_FIELD_EMPLOYEE': {
      const currentCount = state.fieldEmployeesData.employeeCount ?? 0
      if (currentCount >= MAX_ENTITIES) return state
      const newData = { ...state.fieldEmployeesData, employeeCount: currentCount + 1 }
      return {
        ...state,
        fieldEmployeesData: syncFieldEmployees(newData, state.seasonData.hoursPerWeek)
      }
    }

    case 'REMOVE_FIELD_EMPLOYEE': {
      const newEmployees = state.fieldEmployeesData.employees.filter((_, i) => i !== action.index)
      return {
        ...state,
        fieldEmployeesData: {
          employeeCount: newEmployees.length,
          employees: newEmployees
        }
      }
    }

    // Admin Employees
    case 'SET_ADMIN_EMPLOYEE_COUNT': {
      const newData = { ...state.adminEmployeesData, employeeCount: action.count }
      return {
        ...state,
        adminEmployeesData: syncAdminEmployees(newData)
      }
    }

    case 'UPDATE_ADMIN_EMPLOYEE': {
      const newEmployees = [...state.adminEmployeesData.employees]
      newEmployees[action.index] = { ...newEmployees[action.index], [action.field]: action.value }
      return {
        ...state,
        adminEmployeesData: { ...state.adminEmployeesData, employees: newEmployees }
      }
    }

    case 'ADD_ADMIN_EMPLOYEE': {
      const currentCount = state.adminEmployeesData.employeeCount ?? 0
      if (currentCount >= MAX_ENTITIES) return state
      const newData = { ...state.adminEmployeesData, employeeCount: currentCount + 1 }
      return {
        ...state,
        adminEmployeesData: syncAdminEmployees(newData)
      }
    }

    case 'REMOVE_ADMIN_EMPLOYEE': {
      const newEmployees = state.adminEmployeesData.employees.filter((_, i) => i !== action.index)
      return {
        ...state,
        adminEmployeesData: {
          employeeCount: newEmployees.length,
          employees: newEmployees
        }
      }
    }

    // Trucks
    case 'SET_TRUCK_COUNT': {
      const newData = { ...state.trucksData, truckCount: action.count }
      return {
        ...state,
        trucksData: syncTrucks(newData)
      }
    }

    case 'UPDATE_TRUCK': {
      const newTrucks = [...state.trucksData.trucks]
      if (action.field === 'ownershipType') {
        if (action.value === 'Own') {
          newTrucks[action.index] = {
            ...newTrucks[action.index],
            ownershipType: 'Own',
            replacementCost: 80000,
            avgUsefulLife: 7,
            sellPrice: 25000,
            monthlyPayment: null
          }
        } else {
          newTrucks[action.index] = {
            ...newTrucks[action.index],
            ownershipType: 'Lease/Finance',
            replacementCost: null,
            avgUsefulLife: null,
            sellPrice: null,
            monthlyPayment: 1000
          }
        }
      } else {
        newTrucks[action.index] = { ...newTrucks[action.index], [action.field]: action.value }
      }
      return {
        ...state,
        trucksData: { ...state.trucksData, trucks: newTrucks }
      }
    }

    case 'ADD_TRUCK': {
      const currentCount = state.trucksData.truckCount ?? 0
      if (currentCount >= MAX_ENTITIES) return state
      const newData = { ...state.trucksData, truckCount: currentCount + 1 }
      return {
        ...state,
        trucksData: syncTrucks(newData)
      }
    }

    case 'REMOVE_TRUCK': {
      const newTrucks = state.trucksData.trucks.filter((_, i) => i !== action.index)
      return {
        ...state,
        trucksData: {
          truckCount: newTrucks.length,
          trucks: newTrucks
        }
      }
    }

    // Trailers
    case 'SET_TRAILER_COUNT': {
      const newData = { ...state.trailersData, trailerCount: action.count }
      return {
        ...state,
        trailersData: syncTrailers(newData)
      }
    }

    case 'UPDATE_TRAILER': {
      const newTrailers = [...state.trailersData.trailers]
      if (action.field === 'ownershipType') {
        if (action.value === 'Own') {
          newTrailers[action.index] = {
            ...newTrailers[action.index],
            ownershipType: 'Own',
            replacementCost: 10000,
            avgUsefulLife: 10,
            sellPrice: 3500,
            monthlyPayment: null
          }
        } else {
          newTrailers[action.index] = {
            ...newTrailers[action.index],
            ownershipType: 'Lease/Finance',
            replacementCost: null,
            avgUsefulLife: null,
            sellPrice: null,
            monthlyPayment: 1000
          }
        }
      } else {
        newTrailers[action.index] = { ...newTrailers[action.index], [action.field]: action.value }
      }
      return {
        ...state,
        trailersData: { ...state.trailersData, trailers: newTrailers }
      }
    }

    case 'ADD_TRAILER': {
      const currentCount = state.trailersData.trailerCount ?? 0
      if (currentCount >= MAX_ENTITIES) return state
      const newData = { ...state.trailersData, trailerCount: currentCount + 1 }
      return {
        ...state,
        trailersData: syncTrailers(newData)
      }
    }

    case 'REMOVE_TRAILER': {
      const newTrailers = state.trailersData.trailers.filter((_, i) => i !== action.index)
      return {
        ...state,
        trailersData: {
          trailerCount: newTrailers.length,
          trailers: newTrailers
        }
      }
    }

    // Heavy Machinery
    case 'SET_HEAVY_MACHINERY_COUNT': {
      const newData = { ...state.heavyMachineryData, machineryCount: action.count }
      return {
        ...state,
        heavyMachineryData: syncHeavyMachinery(newData)
      }
    }

    case 'UPDATE_HEAVY_MACHINERY': {
      const newMachinery = [...state.heavyMachineryData.machinery]
      if (action.field === 'equipmentName') {
        const defaults = getHeavyMachineryDefaults(action.value)
        newMachinery[action.index] = {
          ...newMachinery[action.index],
          equipmentName: action.value,
          customEquipmentName: action.value === 'Other'
            ? newMachinery[action.index].customEquipmentName
            : undefined,
          replacementCost: defaults.replacementCost,
          avgUsefulLife: defaults.avgUsefulLife,
          sellPrice: defaults.sellPrice
        }
      } else if (action.field === 'ownershipType') {
        if (action.value === 'Own') {
          const defaults = getHeavyMachineryDefaults(newMachinery[action.index].equipmentName)
          newMachinery[action.index] = {
            ...newMachinery[action.index],
            ownershipType: 'Own',
            replacementCost: defaults.replacementCost,
            avgUsefulLife: defaults.avgUsefulLife,
            sellPrice: defaults.sellPrice,
            monthlyPayment: null
          }
        } else {
          newMachinery[action.index] = {
            ...newMachinery[action.index],
            ownershipType: 'Lease/Finance',
            replacementCost: null,
            avgUsefulLife: null,
            sellPrice: null,
            monthlyPayment: 1000
          }
        }
      } else {
        newMachinery[action.index] = { ...newMachinery[action.index], [action.field]: action.value }
      }
      return {
        ...state,
        heavyMachineryData: { ...state.heavyMachineryData, machinery: newMachinery }
      }
    }

    case 'ADD_HEAVY_MACHINERY': {
      const currentCount = state.heavyMachineryData.machineryCount ?? 0
      if (currentCount >= MAX_ENTITIES) return state
      const newData = { ...state.heavyMachineryData, machineryCount: currentCount + 1 }
      return {
        ...state,
        heavyMachineryData: syncHeavyMachinery(newData)
      }
    }

    case 'REMOVE_HEAVY_MACHINERY': {
      const newMachinery = state.heavyMachineryData.machinery.filter((_, i) => i !== action.index)
      return {
        ...state,
        heavyMachineryData: {
          machineryCount: newMachinery.length,
          machinery: newMachinery
        }
      }
    }

    default:
      return state
  }
}
